/**
 * ProcessingStatus.jsx
 * 
 * Real-time processing status component for document intake.
 * Shows progress, status updates, and handles completion.
 */

import React, { useState, useEffect, useRef } from 'react';
import './ProcessingStatus.css';

const ProcessingStatus = ({ auditorId, engagementId, jobId, onComplete, onError }) => {
  const [job, setJob] = useState(null);
  const [logs, setLogs] = useState([]);
  const [isPolling, setIsPolling] = useState(true);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (jobId && isPolling) {
      // Start polling for job status
      pollJobStatus();
      
      // Set up interval for continuous polling
      intervalRef.current = setInterval(pollJobStatus, 2000); // Poll every 2 seconds
      
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [jobId, isPolling]);

  const pollJobStatus = async () => {
    try {
      const response = await fetch(
        `/api/v1/document-intake/job/${jobId}?auditor_id=${auditorId}&engagement_id=${engagementId}`
      );
      
      if (response.ok) {
        const jobData = await response.json();
        setJob(jobData);
        
        // Add log entry for status changes
        addLog(`Job status: ${jobData.status} (${Math.round(jobData.progress * 100)}% complete)`);
        
        // Handle completion
        if (jobData.status === 'completed') {
          setIsPolling(false);
          addLog('Processing completed successfully!');
          
          // Load processed documents
          setTimeout(() => loadProcessedDocuments(), 1000);
        } else if (jobData.status === 'failed') {
          setIsPolling(false);
          addLog(`Processing failed: ${jobData.error_message || 'Unknown error'}`);
          onError(jobData.error_message || 'Processing failed');
        }
      } else {
        throw new Error('Failed to fetch job status');
      }
    } catch (error) {
      addLog(`Error checking status: ${error.message}`);
      if (isPolling) {
        // Continue polling on error, but log it
        console.error('Error polling job status:', error);
      }
    }
  };

  const loadProcessedDocuments = async () => {
    try {
      const response = await fetch(
        `/api/v1/document-intake/documents?auditor_id=${auditorId}&engagement_id=${engagementId}`
      );
      
      if (response.ok) {
        const data = await response.json();
        onComplete(data.documents || []);
      } else {
        throw new Error('Failed to load processed documents');
      }
    } catch (error) {
      onError(`Failed to load results: ${error.message}`);
    }
  };

  const addLog = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prevLogs => [...prevLogs, { timestamp, message }]);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return '⏳';
      case 'processing':
        return '⚙️';
      case 'completed':
        return '✅';
      case 'failed':
        return '❌';
      default:
        return '❓';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return '#f39c12';
      case 'processing':
        return '#3498db';
      case 'completed':
        return '#27ae60';
      case 'failed':
        return '#e74c3c';
      default:
        return '#95a5a6';
    }
  };

  const formatDuration = (startTime) => {
    if (!startTime) return '0s';
    
    const start = new Date(startTime);
    const now = new Date();
    const diffMs = now - start;
    const diffSecs = Math.floor(diffMs / 1000);
    
    if (diffSecs < 60) return `${diffSecs}s`;
    const diffMins = Math.floor(diffSecs / 60);
    const remainingSecs = diffSecs % 60;
    return `${diffMins}m ${remainingSecs}s`;
  };

  if (!job) {
    return (
      <div className="processing-status loading">
        <div className="loading-spinner">⏳</div>
        <p>Loading job status...</p>
      </div>
    );
  }

  return (
    <div className="processing-status">
      <div className="status-header">
        <h2>Processing Documents</h2>
        <p>Your documents are being processed through the universal intake pipeline.</p>
      </div>

      {/* Main Status Card */}
      <div className="status-card">
        <div className="status-overview">
          <div className="status-icon" style={{ color: getStatusColor(job.status) }}>
            {getStatusIcon(job.status)}
          </div>
          <div className="status-details">
            <h3>{job.status.charAt(0).toUpperCase() + job.status.slice(1)}</h3>
            <p>Job ID: {job.job_id}</p>
            <p>Duration: {formatDuration(job.created_at)}</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="progress-section">
          <div className="progress-header">
            <span>Progress</span>
            <span>{Math.round(job.progress * 100)}%</span>
          </div>
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ 
                width: `${job.progress * 100}%`,
                backgroundColor: getStatusColor(job.status)
              }}
            ></div>
          </div>
        </div>

        {/* Document Info */}
        <div className="document-info">
          <h4>Processing Details</h4>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Documents:</span>
              <span className="info-value">{job.document_ids.length}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Started:</span>
              <span className="info-value">{new Date(job.created_at).toLocaleString()}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Last Update:</span>
              <span className="info-value">{new Date(job.updated_at).toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Processing Pipeline Steps */}
      <div className="pipeline-steps">
        <h3>Processing Pipeline</h3>
        <div className="steps-container">
          <div className={`step ${job.progress > 0.2 ? 'completed' : job.progress > 0 ? 'active' : ''}`}>
            <div className="step-icon">📄</div>
            <div className="step-label">Document Ingestion</div>
            <div className="step-description">Converting files to text</div>
          </div>
          <div className={`step ${job.progress > 0.5 ? 'completed' : job.progress > 0.2 ? 'active' : ''}`}>
            <div className="step-icon">🔍</div>
            <div className="step-label">Structure Extraction</div>
            <div className="step-description">Identifying sections and tables</div>
          </div>
          <div className={`step ${job.progress > 0.8 ? 'completed' : job.progress > 0.5 ? 'active' : ''}`}>
            <div className="step-icon">🧠</div>
            <div className="step-label">LLM Optimization</div>
            <div className="step-description">Creating semantic chunks</div>
          </div>
          <div className={`step ${job.progress >= 1.0 ? 'completed' : job.progress > 0.8 ? 'active' : ''}`}>
            <div className="step-icon">✅</div>
            <div className="step-label">Quality Assessment</div>
            <div className="step-description">Validating extraction quality</div>
          </div>
        </div>
      </div>

      {/* Activity Log */}
      <div className="activity-log">
        <h3>Activity Log</h3>
        <div className="log-container">
          {logs.map((log, index) => (
            <div key={index} className="log-entry">
              <span className="log-timestamp">{log.timestamp}</span>
              <span className="log-message">{log.message}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Error Display */}
      {job.status === 'failed' && job.error_message && (
        <div className="error-details">
          <h3>Error Details</h3>
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            <span>{job.error_message}</span>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="status-actions">
        {job.status === 'completed' && (
          <button 
            className="view-results-button"
            onClick={() => loadProcessedDocuments()}
          >
            View Results
          </button>
        )}
        
        {job.status === 'failed' && (
          <button 
            className="retry-button"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        )}
      </div>
    </div>
  );
};

export default ProcessingStatus;
